/**
 * A PRISM graphic without editable source (not made in PRISM Graphics, or a scene this editor cannot restore), or
 * one the user may not edit: the package in the sandboxed preview frame (ADR 0020) with a data form generated from
 * the manifest `schema`, and real-time controls. Nothing can be saved from here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, X } from 'lucide-react';
import { PreviewFrame } from '@prism-graphics/harness/frame/client';
import { api, type PrismOpened } from '../api.ts';
import { t } from '../i18n.ts';
import { readPackageZip, type PackageFiles } from '../preview/unzip.ts';
import { layoutsOfManifest } from '../preview/model.ts';

type Prop = { type?: string; title?: string; default?: unknown; enum?: unknown[]; hidden?: boolean; v_ui?: string; maxLength?: number };
type Requirement = { resolution?: { width?: { ideal?: number }; height?: { ideal?: number } }; frameRate?: { ideal?: number } };

/** The graphic's size and rate from its manifest (v_editor.frameRate, else renderRequirements), as PRISM reads them. */
function formatOf(manifest: Record<string, unknown> | undefined): { size: { width: number; height: number }; frameRate: number } {
  const req = (manifest?.renderRequirements as Requirement[] | undefined)?.[0];
  const rate = (manifest?.v_editor as { frameRate?: { num?: number; den?: number } } | undefined)?.frameRate;
  return {
    size: { width: req?.resolution?.width?.ideal ?? 1920, height: req?.resolution?.height?.ideal ?? 1080 },
    frameRate: rate?.num && rate.den ? rate.num / rate.den : (req?.frameRate?.ideal ?? 50),
  };
}

export function ReadOnlyView({ opened, onClose }: { opened: PrismOpened; onClose: () => void }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const frameRef = useRef<PreviewFrame | null>(null);
  const [pkg, setPkg] = useState<PackageFiles>();
  const format = useMemo(() => formatOf(opened.manifest), [opened.manifest]);
  const [size, setSize] = useState(format.size);
  // A multi-layout package (ADR 0037): the primary first, the others from a select.
  const layouts = useMemo(() => (opened.manifest ? layoutsOfManifest(opened.manifest) : []), [opened.manifest]);
  const [layoutId, setLayoutId] = useState(layouts[0]?.id);
  const layoutSize = (id: string | undefined) => {
    const l = layouts.find((x) => x.id === id);
    return l ? { width: l.width, height: l.height } : undefined;
  };
  const [error, setError] = useState<string>();
  const [status, setStatus] = useState<string>(t('readonly.loading'));
  const props = useMemo(() => Object.entries(((opened.manifest?.schema as { properties?: Record<string, Prop> } | undefined)?.properties ?? {}) as Record<string, Prop>), [opened.manifest]);
  const [data, setData] = useState<Record<string, unknown>>(() => Object.fromEntries(props.map(([k, p]) => [k, p.default])));

  useEffect(() => {
    const frame = new PreviewFrame(iframeRef.current!, { onConsole: (text) => setError(text) });
    frameRef.current = frame;
    let alive = true;
    (async () => {
      try {
        const bytes = await api.packageBytes(opened.packageUrl!);
        const p = await readPackageZip(bytes);
        if (!alive) return;
        setPkg(p);
        const loaded = await frame.loadFiles(p.files, p.manifestPath);
        setSize({ width: loaded.width, height: loaded.height });
        setStatus(t('readonly.loaded'));
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
      frame.dispose();
    };
  }, [opened.packageUrl]);

  const run = useCallback(
    async (method: string, params?: unknown) => {
      const frame = frameRef.current;
      if (!frame || !pkg) return;
      const r = await frame.call(method, params);
      setStatus(`${method}: ${r.error ?? JSON.stringify((r.result as { statusCode?: number } | undefined)?.statusCode ?? 'ok')}`);
    },
    [pkg],
  );

  const play = async () => {
    const frame = frameRef.current;
    if (!frame || !pkg) return;
    await frame.loadFiles(pkg.files, pkg.manifestPath, layoutSize(layoutId));
    await run('load', { data, renderType: 'realtime', renderCharacteristics: { resolution: size, frameRate: format.frameRate, accessToPublicInternet: false } });
    await run('playAction', {});
  };

  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / size.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [size.width]);

  return (
    <div className="readonly" data-testid="readonly-view">
      <header className="topbar" role="banner">
        <div className="brand" aria-hidden>
          PRISM <b>Graphics</b>
        </div>
        <strong className="ro-name" data-testid="readonly-name">
          {opened.name}
        </strong>
        <span className="muted">v{opened.version}</span>
        {layouts.length > 1 && (
          <label className="top-select" title={t('layouts.previewHint')}>
            <span className="sr-only">{t('layouts.preview')}</span>
            <select
              value={layoutId}
              data-testid="readonly-layout"
              aria-label={t('layouts.preview')}
              onChange={(e) => {
                const id = e.target.value;
                setLayoutId(id);
                const frame = frameRef.current;
                if (!frame || !pkg) return;
                void frame.loadFiles(pkg.files, pkg.manifestPath, layoutSize(id)).then((loaded) => setSize({ width: loaded.width, height: loaded.height }), (e2: unknown) => setError(e2 instanceof Error ? e2.message : String(e2)));
              }}
            >
              {layouts.map((l) => (
                <option key={l.id} value={l.id}>
                  {t('layouts.option', { width: l.width, height: l.height, label: l.label })}
                </option>
              ))}
            </select>
          </label>
        )}
        <span className="ro-badge" data-testid="readonly-badge">
          <Eye size={14} aria-hidden /> {t('readonly.badge')}
        </span>
        <div className="spacer" />
        <button type="button" className="btn" onClick={onClose} data-testid="readonly-close">
          <X size={15} aria-hidden /> {t('readonly.close')}
        </button>
      </header>
      <p className="ro-reason" role="note" data-testid="readonly-reason" data-reason={opened.reason}>
        {opened.message}
      </p>
      <div className="ro-body">
        <div className="ro-stage checker" ref={stageRef} style={{ aspectRatio: `${size.width} / ${size.height}`, width: `min(100%, calc((100vh - 160px) * ${size.width / size.height}))` }}>
          <iframe
            ref={iframeRef}
            title={t('readonly.frame')}
            className="preview-frame"
            src="/preview-frame.html"
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            style={{ width: size.width, height: size.height, transform: `scale(${scale})` }}
            data-testid="readonly-frame"
          />
        </div>
        <aside className="ro-form" aria-label={t('readonly.data')}>
          <h2 className="panel-title">{t('readonly.data')}</h2>
          {props
            .filter(([, p]) => !p.hidden)
            .map(([k, p]) => (
              <label key={k} className="field text">
                <span className="field-label">{p.title ?? k}</span>
                {Array.isArray(p.enum) ? (
                  <select value={String(data[k] ?? '')} onChange={(e) => setData({ ...data, [k]: e.target.value })} data-testid={`ro-field-${k}`}>
                    {p.enum.map((v) => (
                      <option key={String(v)} value={String(v)}>
                        {String(v)}
                      </option>
                    ))}
                  </select>
                ) : p.type === 'boolean' ? (
                  <input type="checkbox" checked={data[k] === true} onChange={(e) => setData({ ...data, [k]: e.target.checked })} data-testid={`ro-field-${k}`} />
                ) : (
                  <input
                    type={p.v_ui === 'color' ? 'color' : p.type === 'number' || p.type === 'integer' ? 'number' : 'text'}
                    value={data[k] === undefined ? '' : String(data[k])}
                    maxLength={p.maxLength}
                    onChange={(e) => setData({ ...data, [k]: p.type === 'number' || p.type === 'integer' ? Number(e.target.value) : e.target.value })}
                    data-testid={`ro-field-${k}`}
                  />
                )}
              </label>
            ))}
          <div className="btn-grid">
            <button type="button" className="btn" disabled={!pkg} onClick={() => void play()} data-testid="ro-play">
              {t('readonly.play')}
            </button>
            <button type="button" className="btn" disabled={!pkg} onClick={() => void run('playAction', { delta: 1 })} data-testid="ro-next">
              {t('readonly.next')}
            </button>
            <button type="button" className="btn" disabled={!pkg} onClick={() => void run('updateAction', { data })} data-testid="ro-update">
              {t('readonly.update')}
            </button>
            <button type="button" className="btn" disabled={!pkg} onClick={() => void run('stopAction', {})} data-testid="ro-stop">
              {t('readonly.stop')}
            </button>
          </div>
          <p className="hint" role="status" data-testid="readonly-status">
            {status}
          </p>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
